import {
   ADDITIONAL_SEAT_COST,
   BASE_SEAT_COST,
   EAC_COLD_LEAD,
   EAC_NEW_LEAD,
   MIN_TOP_UP,
} from '@forwardflow/constants/onboarding.constants'
import { useSubscriptionPackageLists } from '@forwardflow/hooks/customHooks'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useEffect, useMemo } from 'react'

export const usePricing = ({ initializeTier = true } = {}) => {
   const { onboardingData, setPricingData } = useOnboardingStoreV2()
   const { data: subscriptionPackages } = useSubscriptionPackageLists({
      page: 1,
      limit: 10,
   })

   const { pricingData } = onboardingData

   const {
      seatsCount,
      newLeadBudget,
      reactivateLeadBudget,
      maxSpend,
   } = pricingData || {
      seatsCount: 1,
      newLeadBudget: 93,
      reactivateLeadBudget: 57,
      maxSpend: 150,
   }

   // Calculate seat cost: $97 for first user + $50 for each additional user
   const seatCost = useMemo(() => {
      return BASE_SEAT_COST + (seatsCount - 1) * ADDITIONAL_SEAT_COST
   }, [seatsCount])

   // Derive lead counts for display
   const newLeadCount = useMemo(() => {
      return Math.floor(newLeadBudget / EAC_NEW_LEAD)
   }, [newLeadBudget])

   const reactivateLeadCount = useMemo(() => {
      return Math.floor(reactivateLeadBudget / EAC_COLD_LEAD)
   }, [reactivateLeadBudget])

   // Balance top-up: max(MIN_TOP_UP, maxSpend)
   const balanceTopUp = useMemo(() => {
      return Math.max(MIN_TOP_UP, maxSpend)
   }, [maxSpend])

   // Day-one charge: seatCost + balanceTopUp
   const chargedToday = useMemo(() => {
      return seatCost + balanceTopUp
   }, [seatCost, balanceTopUp])

   useEffect(() => {
      if (initializeTier && subscriptionPackages) {
         const selectedTier = subscriptionPackages?.pricingTiers?.[0]
         if (selectedTier && pricingData?.selectedTierId !== selectedTier.id) {
            setPricingData({ ...pricingData, selectedTierId: selectedTier.id })
         }
      }
   }, [initializeTier, pricingData, setPricingData, subscriptionPackages])

   // Handler to update pricing data
   const handlePricingDataChange = (
      field: 'seatsCount' | 'newLeadBudget' | 'reactivateLeadBudget' | 'maxSpend',
      value: number
   ) => {
      setPricingData({
         ...pricingData,
         [field]: value,
      })
   }

   return {
      // Constants
      EAC_NEW_LEAD,
      EAC_COLD_LEAD,
      MIN_TOP_UP,

      // Pricing data
      seatsCount,
      maxSpend,
      newLeadBudget,
      reactivateLeadBudget,

      // Derived lead counts (display only)
      newLeadCount,
      reactivateLeadCount,

      // Calculated costs
      seatCost,
      balanceTopUp,
      chargedToday,

      // Handlers
      handlePricingDataChange,
   }
}
